import type { ApiErrorEnvelope, ApiSuccessEnvelope } from './openapiTypes.js';

export const API_PREFIX = '/api/v1' as const;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isApiEnvelope(value: unknown): value is ApiSuccessEnvelope<unknown> | ApiErrorEnvelope {
  return isRecord(value) && 'data' in value && 'meta' in value && 'error' in value;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId?: string;
  readonly details: Record<string, unknown>;

  constructor({ status, code, message, requestId, details = {} }: {
    status: number;
    code: string;
    message: string;
    requestId?: string;
    details?: Record<string, unknown>;
  }) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
  }
}

export function isSessionRequiredError(error: unknown): error is ApiRequestError {
  return error instanceof ApiRequestError && error.status === 401;
}

export class ApiClient {
  private async request<T>(path: string, init: RequestInit = {}): Promise<ApiSuccessEnvelope<T>> {
    if (!path.startsWith('/')) throw new Error('API path must be relative to the accepted same-origin API prefix.');
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body !== undefined && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json');

    const response = await fetch(`${API_PREFIX}${path}`, {
      ...init,
      headers,
      credentials: 'include',
    });

    let payload: unknown;
    try {
      payload = await response.json();
    } catch {
      throw new ApiRequestError({
        status: response.status,
        code: 'INVALID_API_RESPONSE',
        message: 'Backend returned a non-JSON response.',
      });
    }

    if (!isApiEnvelope(payload)) {
      throw new ApiRequestError({
        status: response.status,
        code: 'INVALID_API_ENVELOPE',
        message: 'Backend response does not match the accepted envelope.',
      });
    }

    if (!response.ok || payload.error !== null) {
      const error = payload.error;
      throw new ApiRequestError({
        status: response.status,
        code: error?.code ?? `HTTP_${response.status}`,
        message: error?.message ?? 'Backend request failed.',
        requestId: isRecord(payload.meta) && typeof payload.meta.request_id === 'string' ? payload.meta.request_id : undefined,
        details: error?.details ?? {},
      });
    }

    return payload as ApiSuccessEnvelope<T>;
  }

  get<T>(path: string): Promise<ApiSuccessEnvelope<T>> {
    return this.request<T>(path, { method: 'GET' });
  }

  post<T>(path: string, body?: unknown, headers?: HeadersInit): Promise<ApiSuccessEnvelope<T>> {
    return this.request<T>(path, {
      method: 'POST',
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  }
}
